import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  listLanguages,
  startTranslation,
  translationEstimate,
  translationStyle,
  type Book,
  type Effort,
} from "../api";
import { Amount } from "../components/Amount";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import EffortChips from "./EffortChips";
import LanguagePicker from "./LanguagePicker";
import SamplePanel from "./SamplePanel";
import { targetLanguages, translatableBooks } from "./translation";
import { useGrowToText } from "./useGrowToText";

/**
 * NewTranslation starts a book as a translation of another (requirement 0018):
 * pick one of the library's books, pick a language, see what it costs, start.
 * Tiles and a searchable row of chips — no dropdowns on a phone. The work runs on the server;
 * the new book opens at once and fills in as chapters finish.
 */
export default function NewTranslation({
  books,
  onStarted,
}: {
  books: Book[];
  onStarted: (book: Book) => void;
}) {
  const t = useT();
  const sources = translatableBooks(books);
  const [sourceID, setSourceID] = useState(sources.length === 1 ? (sources[0]?.id ?? "") : "");
  const [language, setLanguage] = useState("");
  // A copy the author owns, or a book that stays derived from the source and is
  // brought up to date by Refresh (ADR-0093). A copy is the default: it is what
  // this flow has always made, and it is the one with no strings attached.
  const [linked, setLinked] = useState(false);
  // The preset chosen, and what the author has typed over its instruction.
  // `typed` null means "untouched", which the server stores as no instruction —
  // so opening this step cannot by itself change how a book is translated.
  const [preset, setPreset] = useState("");
  const [typed, setTyped] = useState<string | null>(null);
  // How hard the model is asked to think (ADR-0097). An argument to the press
  // that spends, never a stored setting, so it starts at the server's default
  // every time this screen is opened.
  const [effort, setEffort] = useState<Effort | null>(null);
  const source = sources.find((b) => b.id === sourceID);

  const languages = useQuery({
    queryKey: ["languages"],
    queryFn: listLanguages,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  // A preset names its own language, so its chip is a destination beside the
  // languages — and choosing one makes the source's own language reachable,
  // which is what an English book into Simple English needs (ADR-0094).
  const style = useQuery({
    queryKey: ["translation-style", language],
    queryFn: () => translationStyle(language),
    enabled: !!sourceID,
  });
  const presets = style.data?.presets ?? [];
  const chosenPreset = presets.find((p) => p.id === preset);
  const targets = targetLanguages(languages.data?.items ?? [], source);
  const chosen = chosenPreset
    ? chosenPreset.language
    : targets.some((l) => l.code === language)
      ? language
      : "";

  // What the step shows: the preset's words, or the author's, or the default.
  const shown = typed ?? chosenPreset?.instruction ?? style.data?.default_instruction ?? "";
  const instructionBox = useGrowToText(shown);
  // What is sent. The untouched default is no instruction at all, and the
  // server decides that too — this only avoids sending a value the author
  // never chose before the default has loaded.
  const instruction = shown === style.data?.default_instruction ? "" : shown;
  // The effort the presses carry: the author's chip, else the server's default.
  const thinking: Effort = effort ?? style.data?.default_effort ?? "medium";

  const estimate = useQuery({
    queryKey: ["translation-estimate", sourceID, chosen, instruction, preset, thinking],
    queryFn: () => translationEstimate(sourceID, chosen, instruction, preset, thinking),
    enabled: !!sourceID && !!chosen,
  });
  const start = useMutation({
    mutationFn: () =>
      startTranslation({
        bookID: sourceID,
        language: chosen,
        instruction,
        preset,
        linked,
        effort: thinking,
      }),
    onSuccess: onStarted,
  });

  if (sources.length === 0) {
    return (
      <p data-testid="translate-no-sources" className="text-sm text-muted">
        {t.translation.noSources}
      </p>
    );
  }

  const cost = estimate.data?.estimate ?? "";
  // Before a price arrives the button says what it does, without one.
  const noCost = !estimate.data;

  return (
    <div data-testid="new-translation">
      <p className="text-xs text-muted">{t.translation.translateNote}</p>

      <h3 className="mt-4 mb-2 text-sm font-medium">{t.translation.from}</h3>
      <ul className="grid grid-cols-2 gap-2">
        {sources.map((b) => (
          <li key={b.id}>
            <button
              type="button"
              data-testid="translate-source"
              aria-pressed={b.id === sourceID}
              onClick={() => setSourceID(b.id)}
              className={`flex h-full w-full items-center gap-2 rounded-control border p-2 text-start transition-colors ${
                b.id === sourceID ? "border-sage bg-sage-soft" : "border-line hover:bg-sunk"
              }`}
            >
              {b.cover_url ? (
                <img
                  src={b.cover_url}
                  alt=""
                  className="h-12 w-9 shrink-0 rounded-sm object-cover"
                />
              ) : (
                <span aria-hidden="true" className="h-12 w-9 shrink-0 rounded-sm bg-sunk" />
              )}
              <span dir="auto" className="line-clamp-2 min-w-0 text-sm leading-snug">
                {b.title}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {source && (
        <>
          <h3 className="mt-4 mb-2 text-sm font-medium">{t.translation.into}</h3>
          <LanguagePicker
            testID="translate-language"
            languages={targets}
            value={chosenPreset ? "" : chosen}
            onChoose={(code) => {
              setPreset("");
              setTyped(null);
              setLanguage(code);
            }}
          />
          {presets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`translate-preset-${p.id}`}
                  aria-pressed={p.id === preset}
                  onClick={() => {
                    setPreset(p.id === preset ? "" : p.id);
                    setTyped(null);
                  }}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                    p.id === preset
                      ? "border-sage bg-sage-soft font-medium"
                      : "border-line hover:bg-sunk"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}

          <h3 className="mt-4 mb-2 text-sm font-medium">{t.translation.kind}</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { linked: false, label: t.translation.copyKind },
              { linked: true, label: t.translation.linkedKind },
            ].map((choice) => (
              <button
                key={String(choice.linked)}
                type="button"
                data-testid={choice.linked ? "translate-linked" : "translate-copy"}
                aria-pressed={linked === choice.linked}
                onClick={() => setLinked(choice.linked)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  linked === choice.linked
                    ? "border-sage bg-sage-soft font-medium"
                    : "border-line hover:bg-sunk"
                }`}
              >
                {choice.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted">
            {linked ? t.translation.linkedNote : t.translation.copyNote}
          </p>

          {chosen && (
            <>
              <h3 className="mt-4 mb-2 text-sm font-medium">{t.translation.howHeading}</h3>
              <p className="mb-2 text-xs text-muted">{t.translation.howNote}</p>
              <label className="sr-only" htmlFor="translate-instruction">
                {t.translation.howHeading}
              </label>
              {/* dir="auto" on the field itself: an author writing a Persian
                  instruction should get a Persian layout, and the block around
                  it carries no direction of its own to override that. */}
              {/* It grows to its text rather than scrolling inside itself: at
                  phone width the default instruction is seven lines, and a box
                  that clipped the last one mid-sentence would hide exactly the
                  part an author is here to read. */}
              <textarea
                id="translate-instruction"
                ref={instructionBox}
                data-testid="translate-instruction"
                dir="auto"
                rows={3}
                maxLength={style.data?.max_runes}
                value={shown}
                onChange={(e) => {
                  setTyped(e.target.value);
                }}
                className="w-full resize-none overflow-hidden rounded-control border border-line bg-surface px-3 py-2 text-sm leading-relaxed"
              />
              {typed !== null && (
                <button
                  type="button"
                  data-testid="translate-instruction-reset"
                  onClick={() => setTyped(null)}
                  className="mt-2 text-xs text-muted underline hover:text-ink"
                >
                  {t.translation.howReset}
                </button>
              )}

              {/* How hard to think, beside the instruction because it is the
                  other half of how the book will read — and carried by Try it,
                  or the sample stops predicting the run (ADR-0097 §4). */}
              <div className="mt-4">
                <h3 className="text-sm font-medium">{t.translation.effortHeading}</h3>
                <p className="mt-1 mb-2 text-xs text-muted">{t.translation.effortNote}</p>
                <EffortChips
                  value={thinking}
                  offered={style.data?.efforts ?? ["fast", "medium", "high"]}
                  onChange={setEffort}
                  testIdPrefix="translate-effort"
                />
              </div>

              {/* The two presses that try it, which are the only paid calls on
                  this screen before Translate (ADR-0099). */}
              <SamplePanel
                testID="translate"
                order={{
                  bookID: sourceID,
                  language: chosen,
                  instruction,
                  preset,
                  effort: thinking,
                }}
              />

              <p className="mt-3 text-xs text-muted">{t.translation.rulesNote}</p>
              {/* The rules are sent in these words, so they are shown in them:
                  a Latin run inside a page that may be Persian, with its own
                  direction and never translated (ADR-0094). */}
              <ul
                data-testid="translate-rules"
                dir="ltr"
                className="mt-1 space-y-1 rounded-control border border-line bg-sunk px-3 py-2 text-start text-xs text-muted"
              >
                {(style.data?.rules ?? []).map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      <p className="mt-3 text-xs text-muted">{t.translation.background}</p>

      {/* Fixed to the screen, not sticky inside a scroller: this used to sit in
          a 70vh sheet and was drawn over the rules text below it. The page
          reserves its height, so nothing is ever underneath it. */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto w-full max-w-3xl">
          {chosen && (
            <p data-testid="translate-estimate" className="text-xs text-muted">
              {estimate.data ? (
                <Amount
                  sentence={(a) => t.translation.estimate(estimate.data.chapters, a)}
                  amount={cost}
                />
              ) : estimate.isError ? null : (
                t.translation.estimating
              )}
            </p>
          )}
          <MutationError
            className="mt-2"
            testId="translate-estimate-error"
            error={estimate.error}
            fallback={t.common.somethingWrong}
          />
          <MutationError
            className="mt-2"
            testId="translate-error"
            error={start.error}
            fallback={t.translation.startFailed}
          />
          <button
            type="button"
            data-testid="translate-start"
            disabled={!estimate.data || start.isPending}
            onClick={() => start.mutate()}
            className="mt-3 w-full rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {noCost ? (
              t.translation.translate
            ) : (
              <Amount sentence={t.translation.start} amount={cost} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
